import { $, gsap, ScrollTrigger } from "../shared/globals.js";
import { getFinalAnimationProps, getExpandingImageFinalAnimationProps } from "../utils/finalAnimationProps.js";
import { getSceneImageClass } from "../utils/getSceneImageClass.js";

export function createHeroTimelineFeature({
    context,
    CONFIG,
    prefersReducedMotion,
    mm,
    marqueeText,
    tigerWalk,
    parallax,
}) {
    const {
        $html,
        $mainScene,
        sceneImageClassPrefix,
        sceneImageClasses,
        $heroTiger,
        $tigerText,
        $floatingImgs,
        $expandingImg,
        $img01,
        $img03,
        $img04,
        $img05,
        $img06,
        $sliderMobile,
        $tigerTextMobile,
    } = context;

    let activeDesktopTimeline = null;
    let activeMobileTimeline = null;
    let activeScrubTrigger = null;
    let desktopLoadDelayId = null;
    let desktopFallbackId = null;
    let mobileLoadDelayId = null;
    let mobileFallbackId = null;

    function cleanupDesktopPlayback() {
        if (desktopLoadDelayId) {
            window.clearTimeout(desktopLoadDelayId);
            desktopLoadDelayId = null;
        }

        if (desktopFallbackId) {
            window.clearTimeout(desktopFallbackId);
            desktopFallbackId = null;
        }

        $(window).off("load.hero-auto");
    }

    function cleanupMobilePlayback() {
        if (mobileLoadDelayId) {
            window.clearTimeout(mobileLoadDelayId);
            mobileLoadDelayId = null;
        }

        if (mobileFallbackId) {
            window.clearTimeout(mobileFallbackId);
            mobileFallbackId = null;
        }

        $(window).off("load.hero-mobile");
    }

    function cleanupDesktopMode() {
        cleanupDesktopPlayback();

        if (activeDesktopTimeline) {
            activeDesktopTimeline.kill();
            activeDesktopTimeline = null;
        }

        if (activeScrubTrigger) {
            activeScrubTrigger.kill();
            activeScrubTrigger = null;
        }

        marqueeText.stopTextAnimation();
        tigerWalk.stopTigerWalking();
        $html.removeClass("is-locked auto-scrolling");
    }

    function cleanupMobileMode() {
        cleanupMobilePlayback();

        if (activeMobileTimeline) {
            activeMobileTimeline.kill();
            activeMobileTimeline = null;
        }

        marqueeText.stopMobileTextAnimation();
        tigerWalk.stopTigerWalking();
    }

    function initMobileMode() {
        if (!$mainScene.length) return;
        const hasHeroTiger = $heroTiger.length > 0;
        const hasMobileSlider = $sliderMobile.length > 0;
        const hasFloatingImages = $floatingImgs.length > 0;

        if (prefersReducedMotion) {
            if (hasHeroTiger) {
                gsap.set($heroTiger[0], { clearProps: "x,y,xPercent,yPercent,rotation,rotationX,rotationY" });
                gsap.set($heroTiger[0], {
                    opacity: 1,
                    scale: 1,
                    x: 0,
                    y: 0,
                    xPercent: 0,
                    yPercent: -50,
                    width: "55vw",
                    maxWidth: "280px",
                });
            }

            if (hasMobileSlider) {
                gsap.set($sliderMobile[0], { opacity: 1, scale: 1 });
            }

            if (!hasMobileSlider && hasFloatingImages) {
                gsap.set($floatingImgs, { opacity: 1, clearProps: "transform" });
            }

            gsap.set($tigerTextMobile, { opacity: 1 });
            $(".tiger-static").addClass("active");
            marqueeText.startMobileTextAnimation();
            return;
        }

        activeMobileTimeline = gsap.timeline({
            paused: true,
        });

        if (hasHeroTiger) {
            gsap.set($heroTiger[0], { clearProps: "x,y,xPercent,yPercent,rotation,rotationX,rotationY" });
            activeMobileTimeline.to($heroTiger[0], {
                opacity: 1,
                scale: 1,
                x: 0,
                y: 0,
                xPercent: 0,
                yPercent: -50,
                width: "55vw",
                maxWidth: "280px",
                duration: 0.8,
                ease: "power2.out",
                onComplete: () => {
                    $(".tiger-static").removeClass("active");
                    tigerWalk.startTigerWalking();
                },
            });
        }

        if (hasMobileSlider) {
            activeMobileTimeline.to(
                $sliderMobile[0],
                {
                    opacity: 1,
                    scale: 1,
                    duration: 0.8,
                    ease: "power2.out",
                },
                hasHeroTiger ? "<" : 0
            );
        } else if (hasFloatingImages) {
            activeMobileTimeline.fromTo(
                $floatingImgs.toArray(),
                {
                    opacity: 0,
                    y: 24,
                    scale: 0.96,
                },
                {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    duration: 0.7,
                    stagger: 0.08,
                    ease: "power2.out",
                    clearProps: "transform",
                },
                hasHeroTiger ? "<" : 0
            );
        }

        activeMobileTimeline.to(
            $tigerTextMobile,
            {
                opacity: 1,
                duration: 0.6,
                ease: "power2.out",
                stagger: 0.15,
                onStart: () => {
                    marqueeText.startMobileTextAnimation();
                },
            },
            hasHeroTiger || hasMobileSlider || hasFloatingImages ? "-=0.35" : 0
        );

        $(window).on("load.hero-mobile", () => {
            mobileLoadDelayId = window.setTimeout(() => {
                activeMobileTimeline?.play();
            }, 100);
        });

        mobileFallbackId = window.setTimeout(() => {
            if (activeMobileTimeline && activeMobileTimeline.paused()) {
                activeMobileTimeline.play();
            }
        }, 800);
    }

    function initAutoMode() {
        if (!$mainScene.length) return;

        if (prefersReducedMotion) {
            gsap.set([$heroTiger[0], $tigerText[0]].filter(Boolean), { opacity: 1 });

            if ($heroTiger.length) {
                gsap.set($heroTiger[0], { scale: 1 });
            }

            $floatingImgs.each(function() {
                gsap.set(this, { opacity: 1, scale: 1 });
            });
            marqueeText.startTextAnimation();
            parallax.enableParallax3D();
            return;
        }

        activeDesktopTimeline = gsap.timeline({
            paused: true,
            onComplete: () => {
                parallax.enableParallax3D();
            },
        });

        buildMainTimeline(activeDesktopTimeline);

        $(window).on("load.hero-auto", () => {
            desktopLoadDelayId = window.setTimeout(() => {
                activeDesktopTimeline?.play();
            }, 100);
        });

        desktopFallbackId = window.setTimeout(() => {
            if (activeDesktopTimeline && activeDesktopTimeline.paused()) {
                activeDesktopTimeline.play();
            }
        }, 1000);
    }

    function initScrubMode() {
        if (!$mainScene.length) return;

        activeDesktopTimeline = gsap.timeline();
        buildMainTimeline(activeDesktopTimeline, true);

        activeScrubTrigger = ScrollTrigger.create({
            animation: activeDesktopTimeline,
            trigger: $mainScene[0],
            start: "top top",
            end: `+=${CONFIG.scrollDistance}`,
            scrub: CONFIG.scrubSmoothness,
            pin: true,
            anticipatePin: 1,
            onEnter: () => {
                parallax.enableParallax3D();
            },
            onLeave: () => {},
            onLeaveBack: () => {},
        });
    }

    function buildMainTimeline(timeline, isScrubMode = false) {
        const durationMultiplier = isScrubMode ? 0.5 : 1;
        const rotations = {
            [`${sceneImageClassPrefix}01`]: 25,
            [`${sceneImageClassPrefix}02`]: -33,
            [`${sceneImageClassPrefix}03`]: 30,
            [`${sceneImageClassPrefix}04`]: 18,
            [`${sceneImageClassPrefix}05`]: -50,
            [`${sceneImageClassPrefix}06`]: 0,
        };

        timeline.to($heroTiger[0], {
            opacity: 1,
            scale: 1,
            duration: CONFIG.tigerAppearDuration * durationMultiplier,
            ease: isScrubMode ? "power2.out" : "back.out(1.7)",
            delay: isScrubMode ? 0 : CONFIG.tigerAppearDelay,
        });

        timeline.to(
            $tigerText.toArray(),
            {
                opacity: 1,
                duration: CONFIG.elementsAppearDuration * durationMultiplier,
                ease: "power2.out",
                onStart: () => {
                    marqueeText.startTextAnimation();
                },
            },
            isScrubMode ? "<0.1" : "-=0.3"
        );

        $floatingImgs.each(function() {
            const $img = $(this);
            const imgClass = getSceneImageClass($img, sceneImageClasses);
            const originalRotate = rotations[imgClass] || 0;

            timeline.fromTo(
                $img[0],
                {
                    opacity: 0,
                    scale: 0.5,
                    rotation: originalRotate + (isScrubMode ? 0 : Math.random() * 10 - 5),
                },
                {
                    opacity: 1,
                    scale: 1,
                    rotation: originalRotate,
                    duration: CONFIG.elementsAppearDuration * durationMultiplier,
                    ease: isScrubMode ? "power2.out" : "back.out(1.4)",
                },
                isScrubMode ? "<" : `-=${CONFIG.elementsAppearDuration - CONFIG.elementsStagger}`
            );
        });

        timeline.to({}, { duration: 0.3 * durationMultiplier });

        timeline.to($heroTiger[0], {
            width: CONFIG.tigerFinalWidth,
            duration: CONFIG.expandDuration * durationMultiplier,
            ease: "power2.inOut",
            onStart: () => {
                tigerWalk.startTigerWalking();
            },
        });

        timeline.to(
            $expandingImg[0],
            {
                ...getExpandingImageFinalAnimationProps($expandingImg, {
                    left: "50%",
                    top: "50%",
                    bottom: "auto",
                    xPercent: -50,
                    yPercent: -50,
                    width: CONFIG.expandingImgFinalWidth,
                    rotation: 0,
                }),
                duration: CONFIG.expandDuration * durationMultiplier,
                ease: "power2.inOut",
            },
            "<"
        );

        timeline.to(
            $img01[0],
            {
                ...getFinalAnimationProps($img01, {
                    x: -30,
                    y: -80,
                    rotation: 15,
                    opacity: CONFIG.backgroundImgOpacity,
                }),
                duration: CONFIG.repositionDuration * durationMultiplier,
                ease: "power2.inOut",
            },
            "<0.1"
        );

        timeline.to(
            $img03[0],
            {
                ...getFinalAnimationProps($img03, {
                    x: -720,
                    y: -140,
                    rotation: -15,
                    opacity: CONFIG.backgroundImgOpacity,
                }),
                duration: CONFIG.repositionDuration * durationMultiplier,
                ease: "power2.inOut",
            },
            "<"
        );

        timeline.to(
            $img04[0],
            {
                ...getFinalAnimationProps($img04, {
                    x: 40,
                    y: -50,
                    rotation: 10,
                    opacity: CONFIG.backgroundImgOpacity,
                }),
                duration: CONFIG.repositionDuration * durationMultiplier,
                ease: "power2.inOut",
            },
            "<"
        );

        timeline.to(
            $img05[0],
            {
                ...getFinalAnimationProps($img05, {
                    x: 30,
                    y: 60,
                    rotation: -45,
                    opacity: CONFIG.backgroundImgOpacity,
                }),
                duration: CONFIG.repositionDuration * durationMultiplier,
                ease: "power2.inOut",
            },
            "<"
        );

        if ($img06.length) {
            timeline.to(
                $img06[0],
                {
                    ...getFinalAnimationProps($img06, {
                        x: 0,
                        y: 0,
                        rotation: 0,
                        opacity: CONFIG.backgroundImgOpacity,
                    }),
                    duration: CONFIG.repositionDuration * durationMultiplier,
                    ease: "power2.inOut",
                },
                "<"
            );
        }

        return timeline;
    }

    function register() {
        mm.add("(min-width: 768px)", () => {
            if (CONFIG.mode === "auto") {
                initAutoMode();
            } else if (CONFIG.mode === "scrub") {
                initScrubMode();
            }

            return () => {
                cleanupDesktopMode();
            };
        });

        mm.add("(max-width: 767px)", () => {
            initMobileMode();

            return () => {
                cleanupMobileMode();
            };
        });
    }

    function destroy() {
        cleanupDesktopMode();
        cleanupMobileMode();
    }

    return {
        register,
        destroy,
    };
}
